import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Modal } from '../ui/Modal';
import { type Employee, EMPLOYEE_ROLE_OPTIONS } from '@/types';
import { listAccessRoles, lookupBitrixUser, type AccessRole } from '@/auth/adminClient';
import { nanoid } from 'nanoid';
import { Trash2, Download, Loader2 } from 'lucide-react';

interface Props { id: string | null; onClose: () => void; }

export function EmployeeEditor({ id, onClose }: Props) {
  const { employees, branches, saveEmployee, currentUser, toast } = useStore();
  const existing = id ? employees.find((e) => e.id === id) : null;
  const [draft, setDraft] = useState<Employee>(existing ?? {
    id: nanoid(), full_name: '', is_active: true, branch_id: branches[0]?.id ?? null,
  });

  // Выдача входа через Битрикс (Bitrix ID + роль доступа) — только для супер-админа.
  const superAdmin = currentUser?.access_role === 'superadmin';
  // Уровень сотрудника меняет только управляющий филиала, руководитель сети и супер-админ
  // (чат 02.11.2026) — остальным (даже с доступом к «Персоналу», например финансисту) показываем
  // текущее значение, но не даём его менять. Сервер это же правило проверяет отдельно.
  const canSetLevel = currentUser?.access_role === 'owner' || currentUser?.access_role === 'branch_manager' || superAdmin;
  const [accessRoles, setAccessRoles] = useState<AccessRole[]>([]);
  const [pulling, setPulling] = useState(false);
  useEffect(() => {
    // Список ролей доступа тянем с сервера только супер-админу (у него есть право).
    if (!superAdmin) return;
    listAccessRoles().then(setAccessRoles).catch(() => setAccessRoles([]));
  }, [superAdmin]);

  // «Подтянуть из Битрикса»: по введённому Bitrix User ID тянем ФИО/должность/телефон/email
  // (только чтение). ФИО перезаписываем (остаётся редактируемым), остальное — только в пустое.
  const pullFromBitrix = async () => {
    const bxId = draft.bitrix_user_id;
    if (!bxId) { toast({ kind: 'error', text: 'Сначала укажите Bitrix User ID' }); return; }
    setPulling(true);
    try {
      const u = await lookupBitrixUser(bxId);
      setDraft((d) => ({
        ...d,
        full_name: u.full_name || d.full_name,
        role: d.role || (u.position || undefined),
        position: d.position || (u.position || undefined),
        phone: d.phone || (u.phone || undefined),
        email: d.email || (u.email || undefined),
      }));
      toast({ kind: 'success', text: `Подтянуто из Битрикса: ${u.full_name || 'без имени'}` });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось подтянуть из Битрикса' });
    } finally {
      setPulling(false);
    }
  };

  const valid = draft.full_name.trim().length > 0;

  return (
    <Modal open onClose={onClose} title={existing ? 'Сотрудник' : 'Новый сотрудник'} size="md"
      footer={<>
        {/* «Уволить», а не физически удалить (чат с pinkpillies 02.11.2026): сотрудник,
            заведённый из Битрикса, иначе возвращается на следующем цикле синхронизации, как
            только встречается активным пользователем/ответственным по сделке. Кроме того,
            физическое удаление ломало бы его имя в прошлых играх/зарплатных отчётах, где он
            уже числится. is_active=false убирает его из списка «Сотрудники» (и из выбора в
            новые игры/ставки), но оставляет карточку резолвиться в истории. */}
        {existing && <button className="btn-ghost text-status-refused mr-auto"
          onClick={() => {
            if (confirm('Уволить сотрудника? Он пропадёт из списка «Сотрудники» и из выбора в новые игры, но останется в прошлых играх и отчётах.')) {
              void saveEmployee({ ...existing, is_active: false });
              onClose();
            }
          }}>
          <Trash2 className="w-4 h-4" /> Уволить
        </button>}
        <button className="btn-ghost" onClick={onClose}>Отмена</button>
        <button className="btn-primary" disabled={!valid} onClick={async () => { await saveEmployee(draft); onClose(); }}>Сохранить</button>
      </>}>
      <div className="space-y-3">
        <div><label className="field-label">ФИО*</label>
          <input className="input" autoFocus value={draft.full_name} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })} />
        </div>
        <div><label className="field-label">Филиал</label>
          <select className="input" value={draft.branch_id ?? ''} onChange={(e) => setDraft({ ...draft, branch_id: e.target.value || null })}>
            <option value="">— любой —</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div><label className="field-label">Должность</label>
          <select className="input" value={draft.role ?? ''} onChange={(e) => setDraft({ ...draft, role: e.target.value || undefined })}>
            <option value="">— не указана —</option>
            {EMPLOYEE_ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            {draft.role && !EMPLOYEE_ROLE_OPTIONS.includes(draft.role as typeof EMPLOYEE_ROLE_OPTIONS[number]) && <option value={draft.role}>{draft.role}</option>}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="field-label">Уровень (ведущий)</label>
            {canSetLevel ? (
              <select className="input" value={draft.level_host ?? ''}
                onChange={(e) => setDraft({ ...draft, level_host: e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null })}>
                <option value="">— не задан —</option>
                <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
              </select>
            ) : (
              <div className="input !bg-surface-2 !text-ink-3">{draft.level_host ?? '— не задан —'}</div>
            )}
          </div>
          <div><label className="field-label">Уровень (диджей)</label>
            {canSetLevel ? (
              <select className="input" value={draft.level_dj ?? ''}
                onChange={(e) => setDraft({ ...draft, level_dj: e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null })}>
                <option value="">— не задан —</option>
                <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
              </select>
            ) : (
              <div className="input !bg-surface-2 !text-ink-3">{draft.level_dj ?? '— не задан —'}</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="field-label">Уровень (соведущий)</label>
            {canSetLevel ? (
              <select className="input" value={draft.level_sovedushiy ?? ''}
                onChange={(e) => setDraft({ ...draft, level_sovedushiy: e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null })}>
                <option value="">— не задан —</option>
                <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
              </select>
            ) : (
              <div className="input !bg-surface-2 !text-ink-3">{draft.level_sovedushiy ?? '— не задан —'}</div>
            )}
          </div>
          <div><label className="field-label">Уровень (хостес)</label>
            {canSetLevel ? (
              <select className="input" value={draft.level_hostes ?? ''}
                onChange={(e) => setDraft({ ...draft, level_hostes: e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null })}>
                <option value="">— не задан —</option>
                <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
              </select>
            ) : (
              <div className="input !bg-surface-2 !text-ink-3">{draft.level_hostes ?? '— не задан —'}</div>
            )}
          </div>
        </div>
        <p className="text-[11px] text-ink-3 -mt-1.5">
          От уровня зависит ставка за игру. Один и тот же человек может быть на разных уровнях как ведущий, диджей, соведущий и хостес.
          {!canSetLevel && ' Менять уровень может только управляющий филиала, руководитель сети или супер-админ.'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="field-label">Телефон</label>
            <input className="input" value={draft.phone ?? ''} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          </div>
          <div><label className="field-label">Email</label>
            <input className="input" value={draft.email ?? ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </div>
        </div>
        {/* Дата рождения — только для чтения здесь: заполняет сам сотрудник в своём
            «Профиле» (чат с Аяужан 03.11.2026: не нашла её в карточке сотрудника). */}
        <div><label className="field-label">Дата рождения</label>
          <div className="input !bg-surface-2 !text-ink-3">
            {draft.birth_date ? new Date(draft.birth_date).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long' }) : '— не заполнена (заполняет сам сотрудник в «Профиле»)'}
          </div>
        </div>

        {/* Выдача входа через Битрикс — только супер-админ. Позволяет пустить человека
            с указанной ролью, даже если его должность в Битриксе доступа не даёт. */}
        {superAdmin && (
          <div className="space-y-3 pt-2 border-t border-line">
            <div>
              <label className="field-label">Bitrix User ID</label>
              <input className="input" type="number" inputMode="numeric" value={draft.bitrix_user_id ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setDraft({ ...draft, bitrix_user_id: v ? Number(v) : undefined });
                }} />
            </div>
            {/* Поле «Роль доступа» убрано по просьбе заказчика (10.07.2026): сбивало с толку —
                его путали с должностью, а роль всё равно назначается в разделе «Доступ».
                Само login_access_role в данных осталось. Вернуть — раскомментировать.
              <div><label className="field-label">Роль доступа (вход через Битрикс)</label>
                <select className="input" value={draft.login_access_role ?? ''}
                  onChange={(e) => setDraft({ ...draft, login_access_role: e.target.value || undefined })}>
                  <option value="">— нет доступа (только справочник) —</option>
                  {accessRoles.filter((r) => r.id !== 'superadmin').map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            */}
            <button type="button" className="btn-soft" disabled={pulling || !draft.bitrix_user_id}
              onClick={() => void pullFromBitrix()}>
              {pulling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Подтянуть из Битрикса
            </button>
            <p className="text-[11px] text-ink-3 leading-snug">
              ID — число из профиля Битрикса (<code>/company/personal/user/ID/</code>).
              Связывает карточку с человеком в Битриксе. Права на вход выдаются
              в разделе «Доступ».
            </p>
          </div>
        )}
        {!superAdmin && draft.bitrix_user_id && (
          <p className="text-[11px] text-ink-3">🔄 Bitrix USER ID: <code>{draft.bitrix_user_id}</code></p>
        )}
      </div>
    </Modal>
  );
}
